// Brand Console DATA -- Entities (real intelligence-graph object viewer).
//
// Consumes ONLY the existing, real, tenant-scoped GET
// /v3/graph/entities/{client_id} route (entitiesAPI.list, already used by
// PaidMediaPage/EcommercePage/SearchDataPage/OutcomesPage for name
// resolution) -- this page is the first place it is shown as its own
// standalone record set, not just used to resolve another page's ids. No
// new backend endpoint, no new service, no new collection.
//
// Read-only by design: EntityService's real CREATE_FIELDS (services.py,
// verified by direct read) are entity_type/canonical_name/aliases/url/
// domain/external_ids/metadata/confidence/source/status -- every one of
// those is rendered here, none invented. There is no per-entity
// relationship endpoint that returns anything beyond entity_ids (the
// relationship service stores from_entity_id/to_entity_id/
// relationship_type only, with no bulk "graph for this client" read
// route), so this stays a flat, searchable table rather than a graph
// visualization -- a diagram would need to invent a layout for data the
// backend does not actually expose as a traversable graph.

import { useState, useEffect, useCallback, useMemo } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { EmptyState, ErrorState, LoadingState } from '@/components/brand/StateViews';
import { StatusBadge, ConfidenceIndicator } from '@/components/brand/Badges';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { WorkspaceSelect } from '@/components/brand/BrandContextBar';
import { useWorkspaceSelection } from '@/hooks/useWorkspaceSelection';
import { entitiesAPI } from '@/lib/intelligenceApi';

function dash(v) {
  return v === null || v === undefined || v === '' ? '—' : v;
}
function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}
function sortRows(rows, key, dir) {
  const copy = [...rows];
  copy.sort((a, b) => {
    const av = a[key];
    const bv = b[key];
    if (av === bv) return 0;
    if (av === null || av === undefined) return 1;
    if (bv === null || bv === undefined) return -1;
    const cmp = av < bv ? -1 : 1;
    return dir === 'asc' ? cmp : -cmp;
  });
  return copy;
}
function SortHeader({ label, col, sort, onSort, testId }) {
  return (
    <th className="px-3 py-2 cursor-pointer select-none whitespace-nowrap" onClick={() => onSort(col)} data-testid={testId}>
      {label}{sort.key === col ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
    </th>
  );
}

export default function EntitiesPage() {
  // P1.9: client resolution is now the shared useWorkspaceSelection() hook
  // (see SignalsPage.js's own comment for the full rationale) instead of
  // an unswitchable first-client fetch.
  const { clients, workspaceId, setWorkspaceId, loadingClients } = useWorkspaceSelection();
  const clientId = workspaceId || null;
  const noClient = !loadingClients && clients.length === 0;
  const [entities, setEntities] = useState(null); // null = loading
  const [error, setError] = useState(null);

  const [filters, setFilters] = useState({ entity_type: '', q: '', includeArchived: false });
  const [appliedFilters, setAppliedFilters] = useState({ entity_type: '', q: '', includeArchived: false });

  const [sort, setSort] = useState({ key: 'canonical_name', dir: 'asc' });
  const [selected, setSelected] = useState(null);

  const load = useCallback((activeFilters) => {
    if (!clientId) return;
    setEntities(null);
    setError(null);
    entitiesAPI.list(clientId, {
      entityType: activeFilters.entity_type || undefined,
      q: activeFilters.q || undefined,
      includeArchived: activeFilters.includeArchived,
    })
      .then((res) => setEntities(res.data?.entities || []))
      .catch((e) => setError(e));
  }, [clientId]);

  useEffect(() => { load(appliedFilters); }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyFilters = (e) => {
    e.preventDefault();
    setAppliedFilters(filters);
    load(filters);
  };
  const clearFilters = () => {
    const cleared = { entity_type: '', q: '', includeArchived: false };
    setFilters(cleared);
    setAppliedFilters(cleared);
    load(cleared);
  };

  const sortedRows = useMemo(() => (entities ? sortRows(entities, sort.key, sort.dir) : []), [entities, sort]);
  const toggleSort = (key) => {
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
  };

  return (
    <DashboardLayout>
      <div data-testid="entities-page" className="max-w-5xl mx-auto">
        <div className="mb-4">
          <h1 className="font-display text-[20px] font-bold text-[var(--text-primary)] mb-1">Entities</h1>
          <p className="text-[12px] text-[var(--text-secondary)]">The real objects Martian's intelligence graph tracks for this workspace -- brands, products, campaigns, keywords, and more.</p>
        </div>

        {!noClient && clients.length > 0 && (
          <div className="mb-4">
            <WorkspaceSelect clients={clients} workspaceId={workspaceId} setWorkspaceId={setWorkspaceId} />
          </div>
        )}

        {noClient && <EmptyState title="ADD A BRAND FIRST" description="Entities appear once Martian has a workspace." />}

        {!noClient && entities === null && !error && <LoadingState />}
        {!noClient && error && <ErrorState error={error} onRetry={() => load(appliedFilters)} />}

        {!noClient && !error && entities !== null && (
          <>
            <form onSubmit={applyFilters} className="flex items-end gap-2 flex-wrap mb-4" data-testid="entities-filters">
              <div>
                <label className="block text-[10px] text-[var(--text-tertiary)] mb-1">Search</label>
                <Input data-testid="filter-entity-q" className="h-8 w-[200px] text-[12px]" value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} placeholder="name or alias" />
              </div>
              <div>
                <label className="block text-[10px] text-[var(--text-tertiary)] mb-1">Entity type</label>
                <Input data-testid="filter-entity-type" className="h-8 w-[140px] text-[12px]" value={filters.entity_type} onChange={(e) => setFilters((f) => ({ ...f, entity_type: e.target.value }))} placeholder="e.g. product" />
              </div>
              <label className="flex items-center gap-1.5 h-8 text-[11px] text-[var(--text-secondary)]">
                <Checkbox data-testid="filter-entity-include-archived" checked={filters.includeArchived} onCheckedChange={(v) => setFilters((f) => ({ ...f, includeArchived: !!v }))} />
                Include archived
              </label>
              <Button type="submit" size="sm" data-testid="apply-entity-filters" className="h-8 text-[12px]">Apply</Button>
              <Button type="button" variant="outline" size="sm" data-testid="clear-entity-filters" className="h-8 text-[12px]" onClick={clearFilters}>Clear filters</Button>
            </form>

            {entities.length === 0 && (
              <EmptyState
                testId="entities-empty"
                title="No entities yet"
                description="Martian creates an entity the first time it has something real to track about it -- a synced product, a connected campaign, a checked keyword. None exist for this workspace yet matching these filters."
              />
            )}

            {entities.length > 0 && (
              <>
                <p className="text-[10px] text-[var(--text-tertiary)] mb-1.5">{entities.length} entit{entities.length === 1 ? 'y' : 'ies'}</p>
                <div className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] overflow-x-auto" data-testid="entities-table">
                  <table className="w-full text-[11px]">
                    <thead>
                      <tr className="border-b border-[var(--border)] text-left text-[var(--text-tertiary)]">
                        <SortHeader label="Name" col="canonical_name" sort={sort} onSort={toggleSort} testId="sort-entity-name" />
                        <SortHeader label="Type" col="entity_type" sort={sort} onSort={toggleSort} testId="sort-entity-type" />
                        <th className="px-3 py-2">Status</th>
                        <SortHeader label="Source" col="source" sort={sort} onSort={toggleSort} testId="sort-entity-source" />
                        <SortHeader label="Created" col="created_at" sort={sort} onSort={toggleSort} testId="sort-entity-created" />
                        <SortHeader label="Updated" col="updated_at" sort={sort} onSort={toggleSort} testId="sort-entity-updated" />
                      </tr>
                    </thead>
                    <tbody>
                      {sortedRows.map((e) => (
                        <tr
                          key={e.id} data-testid={`entity-row-${e.id}`}
                          className="border-b border-[var(--border-light,var(--border))] last:border-b-0 cursor-pointer hover:bg-[var(--surface-sunken,var(--canvas-secondary))] focus-within:bg-[var(--surface-sunken,var(--canvas-secondary))]"
                          onClick={() => setSelected(e)}
                          tabIndex={0}
                          onKeyDown={(ev) => { if (ev.key === 'Enter') setSelected(e); }}
                        >
                          <td className="px-3 py-2 text-[var(--text-primary)] font-medium">{dash(e.canonical_name)}</td>
                          <td className="px-3 py-2 text-[var(--text-secondary)] font-mono">{dash(e.entity_type)}</td>
                          <td className="px-3 py-2">{e.status ? <StatusBadge value={e.status === 'active' ? 'ACTIVE' : e.status.toUpperCase()} /> : '—'}</td>
                          <td className="px-3 py-2 text-[var(--text-secondary)]">{dash(e.source)}</td>
                          <td className="px-3 py-2 text-[var(--text-tertiary)] font-mono whitespace-nowrap">{formatDateTime(e.created_at)}</td>
                          <td className="px-3 py-2 text-[var(--text-tertiary)] font-mono whitespace-nowrap">{formatDateTime(e.updated_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}

        {/* ── Entity detail ── */}
        <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
          <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto bg-[var(--surface)] border-[var(--border)]" data-testid="entity-detail">
            {selected && (
              <>
                <SheetHeader>
                  <SheetTitle className="text-[var(--text-primary)] text-[14px]">{dash(selected.canonical_name)}</SheetTitle>
                  <SheetDescription className="text-[var(--text-tertiary)] text-[11px]">The real, stored entity record.</SheetDescription>
                </SheetHeader>
                <div className="mt-3 space-y-3 text-[12px]">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Type</p><p className="text-[var(--text-primary)] font-mono">{dash(selected.entity_type)}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Status</p><p>{selected.status ? <StatusBadge value={selected.status === 'active' ? 'ACTIVE' : selected.status.toUpperCase()} /> : '—'}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Source</p><p className="text-[var(--text-primary)]">{dash(selected.source)}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Confidence</p><ConfidenceIndicator value={selected.confidence} /></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Domain</p><p className="text-[var(--text-primary)] font-mono">{dash(selected.domain)}</p></div>
                    <div className="col-span-2"><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">URL</p><p className="text-[var(--text-primary)] font-mono break-all">{dash(selected.url)}</p></div>
                  </div>
                  {Array.isArray(selected.aliases) && selected.aliases.length > 0 && (
                    <>
                      <Separator className="bg-[var(--border)]" />
                      <div>
                        <p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Aliases</p>
                        <p className="text-[var(--text-primary)]">{selected.aliases.join(', ')}</p>
                      </div>
                    </>
                  )}
                  {selected.external_ids && Object.keys(selected.external_ids).length > 0 && (
                    <>
                      <Separator className="bg-[var(--border)]" />
                      <div>
                        <p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)] mb-1">External IDs</p>
                        <div className="space-y-1" data-testid="detail-entity-external-ids">
                          {Object.entries(selected.external_ids).map(([k, v]) => (
                            <div key={k} className="flex items-center justify-between text-[11px] bg-[var(--surface-sunken,var(--canvas-secondary))] rounded px-2 py-1">
                              <span className="text-[var(--text-tertiary)] font-mono">{k}</span>
                              <span className="text-[var(--text-primary)] font-mono break-all">{String(v)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                  <Separator className="bg-[var(--border)]" />
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Created</p><p className="text-[var(--text-primary)]">{formatDateTime(selected.created_at)}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Updated</p><p className="text-[var(--text-primary)]">{formatDateTime(selected.updated_at)}</p></div>
                  </div>
                  <Separator className="bg-[var(--border)]" />
                  <div>
                    <p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Record ID</p>
                    <p className="text-[var(--text-tertiary)] font-mono break-all">{dash(selected.id)}</p>
                  </div>
                </div>
              </>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </DashboardLayout>
  );
}
